/**
 * FieldRenderer - рендерит поле настройки по типу из schema
 */
import React from 'react';
import TextSettings from './TextSettings';
import ImageSettings from './ImageSettings';
import SelectFieldSettings from './SelectFieldSettings';
import RangeFieldSettings from './RangeFieldSettings';
import ButtonListSettings from './ButtonListSettings';
import ListSettings from './ListSettings';
import IconPicker from './IconPicker';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import SectionSelect from '../../../components/ui/SectionSelect';

export default function FieldRenderer({
  field,
  value,
  onChange,
  allSections,
  allPages,
  projectId
}) {
  const { type, key, ...config } = field;

  switch (type) {
    case 'text':
      return (
        <TextSettings
          label={config.label}
          value={typeof value === 'object' ? value : { text: value || '' }}
          onChange={onChange}
          placeholder={config.placeholder}
          multiline={config.multiline}
          rows={config.rows}
          defaults={config.defaults}
          showSize={config.showSize}
          showWeight={config.showWeight}
          showColor={config.showColor}
        />
      );

    case 'image':
      return (
        <ImageSettings
          label={config.label}
          value={value}
          onChange={onChange}
          showAlt={config.showAlt}
          showCaption={config.showCaption}
          showUpload={!!projectId}
          showMediaLibrary={!!projectId}
          projectId={projectId}
          hint={config.hint}
        />
      );

    case 'select':
      return (
        <SelectFieldSettings
          label={config.label}
          value={value}
          onChange={onChange}
          options={config.options}
          mode={config.mode}
          columns={config.columns}
        />
      );

    case 'range':
      return (
        <RangeFieldSettings
          label={config.label}
          value={config.fromStorage ? config.fromStorage(value) : value}
          onChange={(v) => onChange(config.toStorage ? config.toStorage(v) : v)}
          min={config.min ?? 0}
          max={config.max ?? 100}
          step={config.step ?? 1}
          unit={config.unit}
        />
      );

    case 'checkbox':
      return (
        <Checkbox
          label={config.label}
          checked={value ?? config.default ?? false}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          label={config.label}
          value={value ?? ''}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          min={config.min}
          max={config.max}
          placeholder={config.placeholder}
        />
      );

    case 'input':
      return (
        <div>
          <Input
            label={config.label}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={config.placeholder}
          />
          {config.hint && (
            <p className="text-xs text-text-secondary mt-1">{config.hint}</p>
          )}
        </div>
      );

    case 'buttonList':
      return (
        <ButtonListSettings
          title={config.label}
          buttons={value || []}
          onChange={onChange}
          allSections={allSections}
          allPages={allPages}
          variants={config.variants}
        />
      );

    case 'icon':
      return (
        <IconPicker
          label={config.label}
          value={value}
          onChange={onChange}
          icons={config.icons}
        />
      );

    case 'list':
      return (
        <ListSettings
          label={config.label}
          value={value || []}
          onChange={onChange}
          itemFields={config.itemFields}
          addLabel={config.addLabel}
          itemLabel={config.itemLabel}
          allSections={allSections}
          allPages={allPages}
          projectId={projectId}
        />
      );

    case 'color':
      return (
        <div>
          <label className="block text-sm font-medium mb-2">{config.label}</label>
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-10 rounded-lg border border-border cursor-pointer"
          />
        </div>
      );

    case 'rating':
      return (
        <div>
          <label className="block text-xs font-medium mb-1.5 text-text-secondary">
            {config.label}
          </label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => onChange(star)}
                className="p-1"
              >
                <Icon
                  name="Star"
                  size={20}
                  className={star <= (value || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                />
              </button>
            ))}
          </div>
        </div>
      );

    case 'link':
      return (
        <div>
          <label className="block text-xs font-medium mb-1.5 text-text-secondary">
            {config.label}
          </label>
          <SectionSelect
            value={value || ''}
            onChange={onChange}
            allSections={allSections}
            placeholder={config.placeholder}
            allowCustom={config.allowCustom !== false}
          />
        </div>
      );

    default:
      return (
        <div className="p-2 bg-warning/10 rounded text-sm text-warning">
          Unknown field type: {type}
        </div>
      );
  }
}
